import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import _ from 'lodash';
import FormSelect from '../../../components/dropdown/formSelect';
import InputText from '../../../components/input-text/inputText';
import ModalComponent from '../../../components/modal/modal';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import {
  CustomerRoleService,
} from '@wealthlane/api-services/wealthlane-customer-service';
import {
  INSTANCE_TENANT_SCOPE,
  type TenantScope,
} from '../../../constants/tenantScope';
import { type ModalStatusType } from '../../../types/common';
import { useAssignInstanceUserRole, useWealthlaneCustomerApi } from '../../../hooks';
import { INSTANCE_USER_ROLE_VALIDATION } from '../../../utils/validations/validations';
import InstanceScopeBanner from '../InstanceScopeBanner';
import { Banner } from '@packages/ui/layout';

export const instanceUserRoleInitialValues = {
  data: {
    fullName: '',
    employeeId: '',
    companyRole: '',
    customerRoleId: null as string | null,
    isDefaultAdmin: false,
  },
  id: '',
};

interface InstanceUserRoleModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  setModalStatus: (value: ModalStatusType) => void;
  usersInitialData: { data: typeof instanceUserRoleInitialValues.data; id: string };
  loading: boolean;
  scope: TenantScope;
}

const InstanceUserRoleModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  setModalStatus,
  usersInitialData,
  loading,
}: InstanceUserRoleModalType): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const [roleList, setRoleList] = useState<Array<{ label: string; value: string }>>([]);
  const [roleLoading, setRoleLoading] = useState(true);

  const { mutate, isLoading } = useAssignInstanceUserRole(
    handleRequest,
    closeModal,
    usersInitialData.id
  );

  useEffect(() => {
    void handleRequest(
      CustomerRoleService.getApiCustomerApiAppCustomerRoleCustomerRole(
        [],
        INSTANCE_TENANT_SCOPE,
        '',
        'RoleName ASC',
        0,
        1000
      )
    )
      .then((data) => {
        if (data?.items != null) {
          setRoleList(
            data.items.map((role) => ({
              label: role.roleName ?? '',
              value: role.id ?? '',
            }))
          );
        }
      })
      .finally(() => {
        setRoleLoading(false);
      });
  }, [handleRequest]);

  const handleSubmit = (values: typeof instanceUserRoleInitialValues.data): void => {
    const customerRoleId =
      values.customerRoleId == null || values.customerRoleId === ''
        ? null
        : values.customerRoleId;
    mutate({ customerRoleId });
  };

  const title =
    modalStatus === 'View'
      ? `View Instance User Role — ${usersInitialData.data.fullName}`
      : modalStatus === 'Edit'
        ? `Edit Instance User Role — ${usersInitialData.data.fullName}`
        : 'Instance User Role';

  return (
    <Formik
      enableReinitialize
      initialValues={usersInitialData.data}
      validationSchema={INSTANCE_USER_ROLE_VALIDATION}
      onSubmit={handleSubmit}>
      {({ touched, handleChange, errors, setFieldValue, values }) => (
        <ModalComponent
          show={isModalOpen}
          closeModal={closeModal}
          title={title}
          loading={loading}
          valueEdited={_.isEqual(values, usersInitialData.data)}
          size='lg'
          id='instanceUserRoleModalContainer'
          children={
            <Form id='instanceUserRoleForm'>
              <ModalBodyComponent id='instanceUserRoleModalBody'>
                <InstanceScopeBanner
                  semanticId='instanceUserRoleScopeBanner'
                  className='mb-[14px]'
                />
                <div className='grid sm:grid-cols-2 gap-x-[14px]'>
                  <InputText
                    type='text'
                    label='Name'
                    name='fullName'
                    id='fullName'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    inputType={modalStatus}
                    disabled
                  />
                  <InputText
                    type='text'
                    label='Employee ID'
                    name='employeeId'
                    id='employeeId'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    inputType={modalStatus}
                    disabled
                  />
                </div>
                <div className='grid sm:grid-cols-2 gap-x-[14px] mt-[14px]'>
                  <InputText
                    type='text'
                    label='Company Role'
                    name='companyRole'
                    id='companyRole'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    inputType={modalStatus}
                    disabled
                  />
                  <FormSelect
                    type='text'
                    label='Instance Role'
                    name='customerRoleId'
                    id='customerRoleId'
                    viewName='customerRole'
                    onChange={(value: string | null) => {
                      setFieldValue('customerRoleId', value);
                    }}
                    errors={errors}
                    touched={touched}
                    options={roleList}
                    isLoading={roleLoading}
                    values={values}
                    setFieldValue={setFieldValue}
                    inputType={modalStatus}
                    disabled={modalStatus === 'View'}
                    isClearable
                  />
                </div>
                <Banner
                  variant='info'
                  semanticId='instanceUserRoleGuidanceBanner'
                  className='mt-[14px]'>
                  {values.customerRoleId != null && values.customerRoleId !== '' ? (
                    <>
                      <strong>Instance role selected.</strong> This role takes preference when using
                      the sandbox. The company role will not be used while an instance role is
                      assigned.
                    </>
                  ) : (
                    <>
                      <strong>No instance role assigned.</strong> The company role takes effect when
                      using the sandbox. Select an instance role to override it for this instance,
                      or clear the field and save to remove an existing assignment.
                    </>
                  )}
                </Banner>
              </ModalBodyComponent>
              <ModalFooterComponent
                id='instanceUserRoleModalFooter'
                modalStatus={modalStatus}
                isEditShow={!usersInitialData.data.isDefaultAdmin}
                onCloseModal={closeModal}
                loading={isLoading}
                valueChanged={_.isEqual(values, usersInitialData.data)}
                onEditModal={() => {
                  setModalStatus('Edit');
                }}
                urlName='users'
              />
            </Form>
          }
        />
      )}
    </Formik>
  );
};

export default InstanceUserRoleModal;
